import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { BRAND_ACCENT } from '../../../presentation/brand';
import { glyph, type GlyphId } from '../../../components/glyphs';
import { seeded } from '../../../components/random';
import { words, revealWords, settleWords } from '../../../components/text';

const SYSTEMS: ReadonlyArray<[GlyphId, string]> = [
  ['document', 'Documents'],
  ['dashboard', 'Dashboards'],
  ['api', 'APIs'],
  ['spreadsheet', 'Spreadsheets'],
  ['message', 'Messages'],
  ['report', 'Reports'],
  ['database', 'Databases'],
  ['cloud', 'Cloud services'],
  ['customer', 'Customers'],
  ['team', 'Teams'],
];

/** Recognisable everyday noise. Deliberately mundane: the audience should see their own organisation. */
const NOISE = [
  'v3_final.xlsx', 'export (2).csv', '#ops-alerts', 'Q3 report', '12,480 rows', 'API · 429',
  'dashboard_old', 'RE: RE: FW: update', 'sync failed', 'meeting notes', 'client_list_new', 'KPI v7',
  'shared drive', 'ticket #4821', 'draft_FINAL.docx', '3 unread threads', 'legacy CRM', 'manual export',
  'approval pending', 'copy of budget', 'webhook retry', 'survey results', 'invoice batch', 'status?',
  'onboarding.pdf', 'backup_2021',
];

type Point = { x: number; y: number };

/** Positions are vw/vh offsets from the viewport centre, the same space as the SVG overlay. */
function scatter(count: number, seed: number, minGap: number): Point[] {
  const random = seeded(seed);
  const points: Point[] = [];
  let guard = 0;
  while (points.length < count && guard < 5000) {
    guard += 1;
    const p = { x: (random() - 0.5) * 86, y: (random() - 0.5) * 74 };
    const inStatementBand = Math.abs(p.x) < 25 && Math.abs(p.y) < 17;
    const tooClose = points.some((q) => Math.hypot(q.x - p.x, (q.y - p.y) * 1.4) < minGap);
    if (!inStatementBand && !tooClose) points.push(p);
  }
  return points;
}

const CHAOS = scatter(SYSTEMS.length, 11, 14);
const NOISE_POSITIONS = scatter(NOISE.length, 29, 6);
const RING: Point[] = SYSTEMS.map((_, i) => {
  const angle = -Math.PI / 2 + (i / SYSTEMS.length) * Math.PI * 2;
  return { x: Math.cos(angle) * 41, y: Math.sin(angle) * 34 };
});

/** Connections that start but never complete. */
const BROKEN = (() => {
  const random = seeded(47);
  const all = [...CHAOS, ...NOISE_POSITIONS];
  return Array.from({ length: 14 }, () => {
    const a = all[Math.floor(random() * all.length)];
    const b = all[Math.floor(random() * all.length)];
    const reach = 0.35 + random() * 0.35;
    return { a, b: { x: a.x + (b.x - a.x) * reach, y: a.y + (b.y - a.y) * reach } };
  });
})();

const LINKS: Array<[number, number]> = [
  ...SYSTEMS.map((_, i) => [i, (i + 1) % SYSTEMS.length] as [number, number]),
  [0, 2], [2, 4], [5, 7], [7, 9],
];

type Mood = 'calm' | 'chaos' | 'frozen' | 'ordered';

/**
 * 0 MORE TOOLS.        — disconnected systems drift
 * 1 MORE DATA.         — noise multiplies
 * 2 MORE COMPLEXITY.   — agitation; connections start and fail
 * 3 THE PROBLEM ISN'T ACCESS. — everything freezes
 * 4 IT'S CONNECTION.   — the systems line up and connect
 */
export class ProblemScene extends BaseScene {
  public readonly stepCount = 5;

  protected template(): string {
    const systems = SYSTEMS.map(([id, label], i) => {
      const p = CHAOS[i];
      return `<div class="sys" style="--x:${p.x.toFixed(2)};--y:${p.y.toFixed(2)};--d:${(5 + (i % 4) * 1.3).toFixed(1)}s;--delay:${(-i * 0.7).toFixed(1)}s">
        <div class="sys__body">${glyph(id)}<span>${label}</span></div>
      </div>`;
    }).join('');

    const noise = NOISE.map((label, i) => {
      const p = NOISE_POSITIONS[i];
      return `<div class="noise" style="--x:${p.x.toFixed(2)};--y:${p.y.toFixed(2)};--d:${(4 + (i % 5)).toFixed(1)}s;--delay:${(-i * 0.43).toFixed(2)}s">
        <div class="noise__body">${label}</div>
      </div>`;
    }).join('');

    const broken = BROKEN.map(({ a }) =>
      `<line class="broken" x1="${a.x}" y1="${a.y}" x2="${a.x}" y2="${a.y}"/>`).join('');

    const links = LINKS.map(([from]) => {
      const a = RING[from];
      return `<line class="link" x1="${a.x}" y1="${a.y}" x2="${a.x}" y2="${a.y}"/>`;
    }).join('');

    return `
      <div class="problem-field" data-mood="calm" aria-hidden="true">
        <svg class="problem-lines" viewBox="-50 -50 100 100" preserveAspectRatio="none">${broken}${links}</svg>
        ${noise}
        ${systems}
      </div>
      <div class="scene-layout problem">
        <div class="statement-stack problem-more">
          <h2 class="statement" data-from="0" data-until="2">${words('MORE TOOLS.')}</h2>
          <h2 class="statement" data-from="1" data-until="2">${words('MORE DATA.')}</h2>
          <h2 class="statement" data-from="2" data-until="2">${words('MORE COMPLEXITY.')}</h2>
        </div>
        <div class="statement-stack problem-thesis">
          <h2 class="statement statement--quiet" data-from="3">${words("THE PROBLEM ISN'T ACCESS.")}</h2>
          <h2 class="statement statement--accent" data-from="4">${words("IT'S CONNECTION.")}</h2>
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$$('.sys'), { opacity: 0, '--s': 0.8 }, { opacity: 1, '--s': 1, duration: 0.9, stagger: 0.09, ease: 'power3.out' }, 0.1);
    tl.add(revealWords(this.$('.problem-more .statement')), 0.5);
  }

  protected onExit(): void {
    this.setMood('calm');
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    // Tension until the systems connect; then the whole atmosphere resolves to the brand colour.
    const accent = step === 4 ? BRAND_ACCENT : this.meta.accent;
    stage.setAccent(accent);
    document.documentElement.style.setProperty('--accent', accent);
    stage.setMode('problem', step, immediate);

    const d = immediate ? 0 : 1;
    const forward = !immediate && step > previousStep;
    const systems = this.$$('.sys');
    const noise = this.$$('.noise');
    const brokenLines = this.$$<SVGLineElement>('.broken');
    const linkLines = this.$$<SVGLineElement>('.link');
    const tl = this.timeline();
    this.stopLoops();

    this.setMood(step <= 1 ? 'calm' : step === 2 ? 'chaos' : step === 3 ? 'frozen' : 'ordered');

    // Systems: scattered until connection, then they take their place on the ring.
    systems.forEach((el, i) => {
      const target = step === 4 ? RING[i] : CHAOS[i];
      gsap.to(el, {
        '--x': target.x,
        '--y': target.y,
        '--s': 1,
        opacity: step === 3 ? 0.3 : 1,
        duration: step === 4 ? d * 1.6 : d * 0.8,
        delay: step === 4 && !immediate ? i * 0.04 : 0,
        ease: 'power3.inOut',
        overwrite: 'auto',
      });
    });

    // Noise: arrives with MORE DATA, crowds in with complexity, dissolves once things connect.
    noise.forEach((el, i) => {
      const home = NOISE_POSITIONS[i];
      const crowd = step === 2 ? 0.8 : 1;
      const visible = step >= 1 && step <= 3;
      gsap.to(el, {
        '--x': home.x * crowd,
        '--y': home.y * crowd,
        '--s': visible ? 1 : 0.6,
        opacity: !visible ? 0 : step === 3 ? 0.14 : 0.75,
        duration: d * (visible ? 0.6 : 0.5),
        delay: step === 1 && forward ? i * 0.045 : 0,
        ease: visible ? 'power3.out' : 'power2.in',
        overwrite: 'auto',
      });
    });

    // Broken connections: reach out, fail, retry. Only while things are chaotic.
    brokenLines.forEach((line, i) => {
      const { a, b } = BROKEN[i];
      const active = step === 2;
      gsap.to(line, {
        attr: { x2: active ? b.x : a.x, y2: active ? b.y : a.y },
        opacity: active ? 0.7 : 0,
        duration: d * 0.7,
        delay: active && !immediate ? 0.3 + i * 0.07 : 0,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    });
    if (step === 2) {
      brokenLines.forEach((line, i) => {
        this.loop(gsap.to(line, {
          opacity: 0.08,
          duration: 0.22 + (i % 5) * 0.09,
          delay: (immediate ? 0 : 1.1) + i * 0.07,
          repeat: -1,
          repeatDelay: 0.3 + (i % 3) * 0.4,
          yoyo: true,
          ease: 'steps(2)',
        }));
      });
    }

    // Real connections: drawn once the systems have settled on the ring.
    linkLines.forEach((line, i) => {
      const [from, to] = LINKS[i];
      const a = RING[from];
      const b = RING[to];
      const active = step === 4;
      gsap.to(line, {
        attr: { x2: active ? b.x : a.x, y2: active ? b.y : a.y },
        opacity: active ? (i < SYSTEMS.length ? 0.85 : 0.35) : 0,
        duration: active ? d * 0.9 : d * 0.3,
        delay: active && !immediate ? 1.3 + i * 0.06 : 0,
        ease: 'power2.inOut',
        overwrite: 'auto',
      });
    });

    this.statementMotion(step, forward, tl);
  }

  private statementMotion(step: number, forward: boolean, tl: gsap.core.Timeline): void {
    const statements = this.$$('.statement');
    statements.forEach((statement) => settleWords(statement));
    if (!forward) return;

    const statement = statements[step];
    tl.add(revealWords(statement, { delay: step === 4 ? 1.1 : 0.15 }), 0);
    if (step === 2) {
      // Complexity arrives with a slight shake of the whole stack.
      tl.fromTo(this.$('.problem-more'), { x: -6 }, { x: 0, duration: 0.5, ease: 'elastic.out(1.2, 0.3)' }, 0.25);
    }
  }

  private setMood(mood: Mood): void {
    this.$('.problem-field').dataset.mood = mood;
  }
}
